/**
 * <s-product-gallery> — PDP main image with a thumbnail row.
 *
 * Shows one [data-gallery-slide] at a time. Clicking a [data-gallery-thumb] or choosing a
 * variant (colour swatch / option) whose featured media is known switches the main slide.
 *
 * Required markup:
 *   [data-gallery-slide="<mediaId>"]          — main stage slides; inactive ones are [hidden].
 *   [data-gallery-thumb="<mediaId>"]          — thumbnail buttons; active one has aria-pressed="true".
 *   script[type="application/json"][data-variant-media] — { "<variantId>": "<mediaId>" }.
 *
 * External coupling:
 *   Listens on document for `change` events from inputs/options carrying data-variant-id
 *   (the same contract <s-variant-picker> uses), because the picker is re-rendered by partials.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #variantMedia = {};

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const mapScript = this.querySelector('script[data-variant-media]');
    try {
      this.#variantMedia = JSON.parse(mapScript?.textContent || '{}');
    } catch {
      this.#variantMedia = {};
    }

    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (!thumb || !this.contains(thumb)) return;
      this.show(thumb.getAttribute('data-gallery-thumb'));
    }, opts);

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement)) return;
      const variantId = input.dataset?.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
      if (!variantId) return;
      const mediaId = this.#variantMedia[variantId];
      if (mediaId) this.show(mediaId);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  show(mediaId) {
    if (!mediaId) return;
    const target = this.querySelector(`[data-gallery-slide="${CSS.escape(mediaId)}"]`);
    if (!target) return;

    for (const slide of this.querySelectorAll('[data-gallery-slide]')) {
      const isTarget = slide === target;
      slide.hidden = !isTarget;
      if (!isTarget) {
        for (const media of slide.querySelectorAll('video')) media.pause?.();
      }
    }
    const targetImage = target.querySelector('img');
    if (targetImage) targetImage.loading = 'eager';

    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      thumb.setAttribute('aria-pressed', String(thumb.getAttribute('data-gallery-thumb') === mediaId));
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
